import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// Guards navigation away from a page with buffered, unsaved edits. The app runs
// on <BrowserRouter> (not a data router), so react-router's useBlocker isn't
// available — instead we cover the real exit vectors ourselves:
//   • browser refresh / tab close / cross-origin nav → the native beforeunload
//     prompt;
//   • in-app SPA nav via anchor links (sidebar / topbar / breadcrumb) → a
//     capture-phase click interceptor that opens the confirm modal;
//   • the page's own back / navigate buttons → wrap them in `guardNavigate`.
//
// The page owns the `dirty` flag and renders the modal from the returned state.
export function useUnsavedChangesGuard(dirty: boolean) {
  const navigate = useNavigate()
  const dirtyRef = useRef(dirty)
  useEffect(() => { dirtyRef.current = dirty }, [dirty])
  const [open, setOpen] = useState(false)
  const pendingRef = useRef<(() => void) | null>(null)

  // Browser-level exits (refresh, tab close, cross-origin). Native prompt only.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  // In-app SPA nav via anchors — intercept during the capture phase so React
  // Router's own delegated click handler never fires, then re-issue the nav
  // through navigate() once the user confirms.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!dirtyRef.current || e.defaultPrevented) return
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const anchor = (e.target as HTMLElement | null)?.closest?.('a')
      if (!anchor) return
      const href = anchor.getAttribute('href')
      const target = anchor.getAttribute('target')
      if (!href || href.startsWith('#') || target === '_blank') return
      let url: URL
      try { url = new URL(href, window.location.href) } catch { return }
      // Leave external links + new-tab alone; ignore same-page hrefs.
      if (url.origin !== window.location.origin) return
      if (url.pathname === window.location.pathname && url.search === window.location.search) return
      e.preventDefault()
      e.stopPropagation()
      pendingRef.current = () => navigate(url.pathname + url.search + url.hash)
      setOpen(true)
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [navigate])

  // Wrap the page's own exit affordances (back button, programmatic navigate).
  const guardNavigate = useCallback((run: () => void) => {
    if (!dirtyRef.current) { run(); return }
    pendingRef.current = run
    setOpen(true)
  }, [])

  // "Leave & discard" — run the staged navigation. It's programmatic, so the
  // click interceptor won't re-catch it even though `dirty` is still true.
  const confirmLeave = useCallback(() => {
    const run = pendingRef.current
    pendingRef.current = null
    setOpen(false)
    run?.()
  }, [])

  const cancelLeave = useCallback(() => {
    pendingRef.current = null
    setOpen(false)
  }, [])

  return { guardOpen: open, guardNavigate, confirmLeave, cancelLeave }
}
